import { Injectable } from '@angular/core';
import { environment } from '@environment';
import { SuggestionId } from '@models/suggestion-id.type';
import { Action, NgxsOnInit, Selector, State, StateContext, StateToken, Store } from '@ngxs/store';
import { append, removeItem } from '@ngxs/store/operators';
import { DossierState, DossierStateModel } from '@stores/dossier/dossier.state';
import { EligibleSuggestionsSet } from '@stores/eligible-suggestion/eligible-suggestion.action';
import {
  EligibleSuggestionState,
  EligibleSuggestionStateModel,
} from '@stores/eligible-suggestion/eligible-suggestion.state';
import { JwtState } from '@stores/jwt/jwt.state';
import { SuggestionGetByMissingIds, SuggestionGetEligible } from '@stores/suggestion/suggestion.action';
import { Suggestion, SuggestionsListing } from '@wizbii/models';
import { SuggestionsWebservice } from '@wizbii/webservices';
import { EMPTY, throwError } from 'rxjs';
import { catchError, filter, map, switchMap, tap } from 'rxjs/operators';
import { Navigate } from '@ngxs/router-plugin';
import { FeaturesRoutingEnum } from '@features/features-routing.enum';

export type SuggestionStateModel = Suggestion[];
export const SuggestionStateToken = new StateToken<SuggestionStateModel>('suggestions');

@State({
  name: SuggestionStateToken,
  defaults: null,
})
@Injectable()
export class SuggestionState implements NgxsOnInit {
  @Selector([DossierState, SuggestionState])
  static dossier(dossierState: DossierStateModel, suggestionState: SuggestionStateModel): Suggestion[] {
    return dossierState
      ?.map((dossier) => suggestionState?.find((suggestion) => suggestion.suggestionId === dossier.suggestionId))
      ?.filter((suggestion) => !!suggestion);
  }

  @Selector([EligibleSuggestionState.notPurchased, SuggestionState])
  static eligible(eligibleSuggestionState: SuggestionId[], suggestionState: SuggestionStateModel): Suggestion[] {
    return eligibleSuggestionState
      ?.map((suggestionId) => suggestionState?.find((suggestion) => suggestion.suggestionId === suggestionId))
      .filter((suggestion) => !!suggestion);
  }

  constructor(private readonly store: Store, private readonly suggestionWebservice: SuggestionsWebservice) {}

  ngxsOnInit(ctx?: StateContext<SuggestionStateModel>): any {
    this.store
      .select(JwtState)
      .pipe(
        filter((tokens) => !!tokens),
        switchMap(() => this.store.dispatch(new SuggestionGetEligible())),
        switchMap(() => this.store.select(DossierState)),
        switchMap((suggestionIds) => this.store.dispatch(new SuggestionGetByMissingIds(suggestionIds)))
      )
      .subscribe();
  }

  @Action(SuggestionGetEligible)
  suggestionGet(ctx: StateContext<SuggestionStateModel>) {
    return this.store.select(JwtState.userId).pipe(
      filter((userId) => !!userId),
      // Get and Store suggestions
      switchMap((userId) => this.suggestionWebservice.getSuggestions(environment.suggestionSetId, userId, 'member')),
      tap((suggestionListing) => {
        if (!!ctx.getState()) {
          // We delete suggestion that have been recalculated to be repush in store
          suggestionListing.eligibleSuggestions.forEach((suggestion) =>
            ctx.setState(removeItem<Suggestion>((item) => item.suggestionId === suggestion.suggestionId))
          );
        }

        ctx.setState(append(suggestionListing.eligibleSuggestions));
      }),

      // Store user eligible suggestions
      map<SuggestionsListing, EligibleSuggestionStateModel>((suggestionListing) => ({
        suggestionIds: suggestionListing.eligibleSuggestions.map((suggestion) => suggestion.suggestionId),
        totalReward: suggestionListing.rewards,
      })),
      switchMap((eligibleSuggestions) => ctx.dispatch(new EligibleSuggestionsSet(eligibleSuggestions))),
      catchError((error) => {
        if (error.error.status === 400) {
          console.error(error);
          return ctx
            .dispatch(new EligibleSuggestionsSet({ suggestionIds: [], totalReward: 0 }))
            .pipe(switchMap(() => ctx.dispatch(new Navigate(['/', FeaturesRoutingEnum.HelpForm]))));
        }
        return throwError(error);
      })
    );
  }

  @Action(SuggestionGetByMissingIds)
  suggestionGetByIds(ctx: StateContext<SuggestionStateModel>, action: SuggestionGetByMissingIds) {
    const missingIds = action.suggestionIds?.filter((suggestionId) => !this.has(suggestionId, ctx));
    if (!missingIds?.length || missingIds.length === 0) {
      return EMPTY;
    }
    return this.suggestionWebservice
      .getSuggestionByIds(missingIds)
      .pipe(tap((suggestions) => ctx.setState(append(suggestions))));
  }

  private has(suggestionId: SuggestionId, ctx: StateContext<SuggestionStateModel>): boolean {
    return !!ctx.getState()?.find((suggestion) => suggestion.suggestionId === suggestionId);
  }
}
